import { useIntl } from "react-intl";

import Avatar from "components/v2/Avatar/Avatar";
import Badge from "components/v2/Badge/Badge";
import Divider from "components/v2/Divider/Divider";
import Dropdown from "components/v2/Dropdown/Dropdown";
import testIds from "constants/testIds";
import { ProviderInviteStatus } from "../../../../../../constants";
import { getStatusOptions } from "../../../helpers/invitedProviderHelper";

type InvitedProviderItemProps = {
  providerName: string;
  providerLogo: string;
  status: typeof ProviderInviteStatus[keyof typeof ProviderInviteStatus];
  providerTeam?: string;
  invitedDate?: string;
  hideDivider?: boolean;
  onRemoveProvider?: () => void;
  onManuallyAddQuote?: () => void;
};

const InvitedProviderItem = function (props: InvitedProviderItemProps) {
  const {
    providerName,
    providerLogo,
    status,
    invitedDate,
    providerTeam,
    hideDivider,
    onRemoveProvider,
    onManuallyAddQuote,
  } = props;
  const intl = useIntl();
  const { color, label } = getStatusOptions(intl)[status];

  return (
    <>
      <div className="invited-provider-item">
        <div className="d-flex align-items-center">
          <div className="mr-xl">
            <Avatar size="sm" image={providerLogo} name={providerName} />
          </div>
          <div>
            <div className="text-sm text-primary">{providerName}</div>
            {providerTeam && <div className="text-sm text-tertiary">{providerTeam}</div>}
          </div>
        </div>
        <div className="d-flex ml-xl">
          <div className="invited-provider-item__status-container">
            <Badge color={color} label={label} type="status-badge" size="md" />
            {invitedDate && (
              <div className="mt-xs text-2xs text-tertiary" style={{ whiteSpace: "nowrap" }}>
                {intl.formatMessage(
                  {
                    id: "shipment-quotes.components.invitedProviders.invitedOn",
                    defaultMessage: "Invited on {date}",
                  },
                  { date: invitedDate },
                )}
              </div>
            )}
          </div>
          <div className="ml-xs py-xxs">
            <Dropdown
              alignRight
              useMenuIcon
              disabled={!onManuallyAddQuote && !onRemoveProvider}
              triggerTestId={testIds.SHIPMENT_DASHBOARD_PROVIDER_MORE_OPTIONS_BUTTON}
              options={[
                {
                  value: "manually_add_quote",
                  disabled: !onManuallyAddQuote,
                  label: (
                    <span className="text-sm text-secondary-v2 font-medium">
                      {intl.formatMessage({
                        id: "shipment-quotes.components.invitedProviders.manuallyAddQuote",
                        defaultMessage: "Manually add quote",
                      })}
                    </span>
                  ),
                  onClick: onManuallyAddQuote,
                },
                {
                  value: "remove_provider",
                  disabled: !onRemoveProvider,
                  label: (
                    <span
                      className="text-sm text-error-primary font-medium"
                      data-testid={testIds.SHIPMENT_DASHBOARD_REVOKE_PROVIDER_INVITE_BUTTON}
                    >
                      {intl.formatMessage({
                        id: "shipment-quotes.components.invitedProviders.removeProvider",
                        defaultMessage: "Remove provider",
                      })}
                    </span>
                  ),
                  onClick: onRemoveProvider,
                },
              ]}
            />
          </div>
        </div>
      </div>
      {!hideDivider && (
        <div className="ml-3xl">
          <Divider />
        </div>
      )}
    </>
  );
};

export default InvitedProviderItem;

InvitedProviderItem.defaultProps = {
  hideDivider: false,
  providerTeam: undefined,
  invitedDate: undefined,
  onRemoveProvider: undefined,
  onManuallyAddQuote: undefined,
};
